// RelatedServicesInLocation — cross-service link layer for combo pages.
// On a service × location combo (e.g. /translate/english/in/bangkok-hq),
// surfaces the SAME location across the OTHER service categories
// (notary, apostille, police-clearance), wiring a dense per-location hub
// graph for both topical authority and user navigation.
import { Link } from "@tanstack/react-router";
import { ArrowRight, Languages, ScrollText, Stamp, ShieldCheck } from "lucide-react";
import { getLocation } from "@/data/locations";
import { POLICE_COMBO_LOC_SLUGS } from "@/routes/police-clearance.$type.index";

export type ServiceKind = "translate" | "notary" | "apostille" | "police";

interface Props {
  currentService?: ServiceKind;
  locationSlug: string;
  locale?: "th" | "en";
}

const DEFAULTS = {
  translateLang: "english",
  notaryAct: "affidavit",
  policeType: "pcsc-thailand-local",
} as const;

interface Card {
  kind: ServiceKind;
  to: string;
  titleTh: string;
  titleEn: string;
  blurbTh: string;
  blurbEn: string;
  Icon: typeof Languages;
}

function buildCards(loc: ReturnType<typeof getLocation>, locale: "th" | "en"): Card[] {
  if (!loc) return [];
  const prefix = locale === "en" ? "/en" : "";
  const policeOk = (POLICE_COMBO_LOC_SLUGS as readonly string[]).includes(loc.slug);

  const cards: Card[] = [
    {
      kind: "translate",
      to: `${prefix}/translate/${DEFAULTS.translateLang}/in/${loc.slug}`,
      titleTh: `แปลเอกสารรับรอง · ${loc.nameTh}`,
      titleEn: `Certified Translation · ${loc.nameEn}`,
      blurbTh: "18 ภาษา · นักแปลขึ้นทะเบียน MoJ/NAATI · ใช้ยื่นได้จริง",
      blurbEn: "18 languages · MoJ/NAATI sworn translators · accepted by embassies",
      Icon: Languages,
    },
    {
      kind: "notary",
      to: `${prefix}/notary/${DEFAULTS.notaryAct}/in/${loc.slug}`,
      titleTh: `Notary Public · ${loc.nameTh}`,
      titleEn: `Notary Public · ${loc.nameEn}`,
      blurbTh: "Affidavit · POA · Certified Copy โดยทนายขึ้นทะเบียนสภาทนายความ",
      blurbEn: "Affidavit · POA · Certified Copy by Lawyers Council-registered notaries",
      Icon: ScrollText,
    },
    {
      kind: "apostille",
      to: `${prefix}/apostille`,
      titleTh: `Apostille / MFA · ${loc.nameTh}`,
      titleEn: `Apostille / MFA · ${loc.nameEn}`,
      blurbTh: "รับรองกระทรวงการต่างประเทศ + Apostille ตามอนุสัญญา Hague",
      blurbEn: "MFA legalization + Hague Apostille for 130 countries",
      Icon: Stamp,
    },
    {
      kind: "police",
      to: policeOk
        ? `${prefix}/police-clearance/${DEFAULTS.policeType}/in/${loc.slug}`
        : `${prefix}/police-clearance`,
      titleTh: `Police Clearance · ${loc.nameTh}`,
      titleEn: `Police Clearance · ${loc.nameEn}`,
      blurbTh: "ตรวจประวัติอาชญากรรม · สตช./FBI/ACRO/AFP/RCMP",
      blurbEn: "Criminal background check · RTP/FBI/ACRO/AFP/RCMP",
      Icon: ShieldCheck,
    },
  ];
  return cards;
}

export function RelatedServicesInLocation({ currentService, locationSlug, locale = "th" }: Props) {
  const loc = getLocation(locationSlug);
  if (!loc) return null;
  const all = buildCards(loc, locale);
  const cards = currentService ? all.filter((c) => c.kind !== currentService) : all;
  if (cards.length === 0) return null;

  const heading = locale === "en"
    ? `Other services in ${loc.nameEn}`
    : `บริการอื่นใน${loc.nameTh}`;
  const sub = locale === "en"
    ? `Same trusted team — notary, MFA legalization, translation and police clearance, all under one roof.`
    : `ทีมเดียวกัน — Notary, Apostille/MFA, แปลเอกสาร และ Police Clearance ครบในที่เดียว`;

  return (
    <section className="border-t border-slate-200 bg-gradient-to-b from-slate-50/60 to-white py-12 sm:py-14">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-700">
          {locale === "en" ? "All services · one address" : "ครบทุกบริการ · ที่เดียว"}
        </div>
        <h2 className="mt-3 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{heading}</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">{sub}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {cards.map((c) => {
            const Icon = c.Icon;
            return (
              <Link
                key={c.kind}
                to={c.to}
                className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="flex-1">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {locale === "en" ? c.titleEn : c.titleTh}
                  </div>
                  <div className="mt-1 text-[12px] leading-snug text-slate-600">
                    {locale === "en" ? c.blurbEn : c.blurbTh}
                  </div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
